import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { DollarSign, Calendar, User, Package, Bell, ChevronRight, CreditCard, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantHighTicket() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const fetchData = async () => {
    try {
      const [ordersRes, productsRes] = await Promise.all([
        api.get('/tenant/high-ticket/orders'),
        api.get('/tenant/products')
      ]);
      setOrders(ordersRes.data.orders || []);
      setProducts(productsRes.data.products?.filter(p => p.is_high_ticket) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const sendReminder = async (scheduleId) => {
    try {
      await api.post(`/tenant/high-ticket/send-reminder/${scheduleId}`);
      toast.success('Reminder terkirim');
    } catch (e) {
      toast.error('Gagal mengirim reminder');
    }
  };

  const viewOrderDetail = async (orderId) => {
    try {
      const res = await api.get(`/tenant/high-ticket/orders/${orderId}`);
      setSelectedOrder(res.data);
      setShowDetailDialog(true);
    } catch (e) {
      toast.error('Gagal memuat detail order');
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      booking_paid: 'bg-blue-100 text-blue-700',
      dp_paid: 'bg-violet-100 text-violet-700',
      in_progress: 'bg-cyan-100 text-cyan-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-700'}>{status}</Badge>;
  };

  const getScheduleStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      paid: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700'
    };
    return <Badge variant="outline" className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
      </div>
    );
  }

  return (
    <div data-testid="tenant-high-ticket-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>High-Ticket Sales</h1>
        <p className="text-sm text-slate-500">Kelola penjualan produk high-ticket dengan sistem cicilan</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{products.length}</p>
                <p className="text-xs text-slate-500">Produk High-Ticket</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <CreditCard size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
                <p className="text-xs text-slate-500">Total Orders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(orders.reduce((sum, o) => sum + (o.total_paid || 0), 0))}</p>
                <p className="text-xs text-slate-500">Total Terbayar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(orders.reduce((sum, o) => sum + (o.remaining || 0), 0))}</p>
                <p className="text-xs text-slate-500">Sisa Piutang</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="products">Produk High-Ticket ({products.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {orders.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  <CreditCard size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Belum ada order high-ticket</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {orders.map((order) => {
                    const progress = order.total_amount > 0 ? (order.total_paid / order.total_amount) * 100 : 0;
                    return (
                      <div key={order.id} className="p-4 hover:bg-slate-50/50 cursor-pointer" onClick={() => viewOrderDetail(order.id)}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center">
                              <Package size={24} className="text-violet-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{order.product_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <User size={12} className="text-slate-400" />
                                <span className="text-sm text-slate-600">{order.customer_name}</span>
                                <span className="text-xs text-slate-400">•</span>
                                <span className="text-sm text-slate-500">{order.customer_email}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              {getStatusBadge(order.status)}
                              <ChevronRight size={16} className="text-slate-400" />
                            </div>
                            <p className="text-lg font-bold text-slate-900 mt-1">Rp {fmt(order.total_amount)}</p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">Progress pembayaran</span>
                            <span className="font-medium text-slate-900">{progress.toFixed(0)}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                          <div className="flex justify-between text-xs text-slate-500 mt-1">
                            <span>Terbayar: Rp {fmt(order.total_paid)}</span>
                            <span>Sisa: Rp {fmt(order.remaining)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <Card key={p.id} className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Package size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      <p className="text-lg font-bold text-[#2D6CDF]">Rp {fmt(p.price)}</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Booking Fee</span>
                      <span className="font-medium">
                        {p.booking_fee_type === 'percentage' ? `${p.booking_fee_value}%` : `Rp ${fmt(p.booking_fee_value)}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">DP</span>
                      <span className="font-medium">{p.dp_percentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Cicilan</span>
                      <span className="font-medium">{p.installment_count}x ({p.installment_type})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Late Policy</span>
                      <span className="font-medium capitalize">{p.late_policy || 'none'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {products.length === 0 && (
              <div className="col-span-full py-12 text-center text-slate-400">
                <p>Belum ada produk high-ticket. Buat produk dengan opsi "High-Ticket" diaktifkan.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Order High-Ticket</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Produk</p>
                  <p className="font-semibold text-slate-900">{selectedOrder.order?.product_name}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="font-bold text-lg text-[#2D6CDF]">Rp {fmt(selectedOrder.order?.total_amount)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="p-4 border border-slate-200 rounded-lg">
                <p className="font-semibold text-slate-900 mb-2">Customer</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Nama:</span> {selectedOrder.order?.customer_name}</div>
                  <div><span className="text-slate-500">Email:</span> {selectedOrder.order?.customer_email}</div>
                  <div><span className="text-slate-500">Phone:</span> {selectedOrder.order?.customer_phone}</div>
                </div>
              </div>

              {/* Payment Schedule */}
              <div>
                <p className="font-semibold text-slate-900 mb-3">Jadwal Pembayaran</p>
                <div className="space-y-2">
                  {selectedOrder.payment_schedule?.map((s, i) => {
                    const isOverdue = s.status !== 'paid' && new Date(s.due_date) < new Date();
                    return (
                      <div key={s.id} className={`p-3 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${s.status === 'paid' ? 'bg-emerald-100 text-emerald-600' : isOverdue ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                              {i + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 capitalize">
                                {s.type === 'installment' ? `Cicilan ${s.installment_number}` : s.type.toUpperCase()}
                              </p>
                              <p className="text-xs text-slate-500">
                                <Calendar size={10} className="inline mr-1" />
                                {new Date(s.due_date).toLocaleDateString('id-ID')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <p className="font-semibold text-slate-900">Rp {fmt(s.amount)}</p>
                              {getScheduleStatusBadge(isOverdue && s.status !== 'paid' ? 'overdue' : s.status)}
                            </div>
                            {s.status !== 'paid' && (
                              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); sendReminder(s.id); }}>
                                <Bell size={14} className="mr-1" /> Remind
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
